import { GeoBounds, RainfallStation } from "./types";
import {
  DEFAULT_IDW_POWER,
  STATION_BUFFER_KM,
  stationBufferDegrees,
} from "./constants";

export function estimateMaxDistanceDegrees(
  stations?: RainfallStation[],
  bounds?: GeoBounds,
  bufferKm: number = STATION_BUFFER_KM
): number {
  const minBuffer = stationBufferDegrees(bufferKm);

  if (
    bounds &&
    Number.isFinite(bounds.north) &&
    Number.isFinite(bounds.south) &&
    Number.isFinite(bounds.east) &&
    Number.isFinite(bounds.west) &&
    bounds.north > bounds.south &&
    bounds.east > bounds.west
  ) {
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;
    const diagonal = Math.sqrt(latSpan * latSpan + lngSpan * lngSpan);
    return Math.max(diagonal * 1.05, minBuffer);
  }

  if (stations && stations.length > 1) {
    let maxSpan = 0;
    for (let i = 0; i < stations.length; i += 1) {
      for (let j = i + 1; j < stations.length; j += 1) {
        const dLat = stations[i].lat - stations[j].lat;
        const dLng = stations[i].lng - stations[j].lng;
        maxSpan = Math.max(maxSpan, Math.sqrt(dLat * dLat + dLng * dLng));
      }
    }
    return Math.max(maxSpan * 1.05, minBuffer);
  }

  return minBuffer;
}

export function interpolateIdwDetailed(
  lat: number,
  lng: number,
  stations: RainfallStation[],
  options?: {
    power?: number;
    maxDistanceDegrees?: number;
  }
): { value: number; nearestDist: number } | null {
  if (!stations.length) {
    return null;
  }

  const power = options?.power ?? DEFAULT_IDW_POWER;
  const maxDistance =
    options?.maxDistanceDegrees ?? stationBufferDegrees(STATION_BUFFER_KM);

  let nearestDist = Number.POSITIVE_INFINITY;
  let nearestValue = stations[0].precipitacaoMm;
  let numerator = 0;
  let denominator = 0;

  for (let i = 0; i < stations.length; i += 1) {
    const station = stations[i];
    const dLat = lat - station.lat;
    const dLng = lng - station.lng;
    const d = Math.sqrt(dLat * dLat + dLng * dLng);

    if (d < nearestDist) {
      nearestDist = d;
      nearestValue = station.precipitacaoMm;
    }

    if (d > maxDistance) {
      continue;
    }

    if (d < 1e-12) {
      return { value: station.precipitacaoMm, nearestDist: 0 };
    }

    const weight = 1 / Math.pow(d, power);
    numerator += weight * station.precipitacaoMm;
    denominator += weight;
  }

  if (nearestDist > maxDistance) {
    return null;
  }

  if (denominator === 0) {
    return { value: nearestValue, nearestDist };
  }

  return { value: numerator / denominator, nearestDist };
}

export function interpolateIdw(
  lat: number,
  lng: number,
  stations: RainfallStation[],
  options?: {
    power?: number;
    maxDistanceDegrees?: number;
  }
): number | null {
  const result = interpolateIdwDetailed(lat, lng, stations, options);
  return result ? result.value : null;
}

export function blurValueGrid(
  values: Float32Array,
  width: number,
  height: number,
  radius: number
): Float32Array {
  if (!(radius > 0) || width <= 0 || height <= 0) {
    return values;
  }

  const kernel = Math.max(1, Math.round(radius));
  const temp = new Float32Array(values.length);
  const output = new Float32Array(values.length);

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let sum = 0;
      let count = 0;
      const x0 = Math.max(0, x - kernel);
      const x1 = Math.min(width - 1, x + kernel);
      for (let ix = x0; ix <= x1; ix += 1) {
        const v = values[y * width + ix];
        if (Number.isFinite(v)) {
          sum += v;
          count += 1;
        }
      }
      temp[y * width + x] = count > 0 ? sum / count : Number.NaN;
    }
  }

  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let sum = 0;
      let count = 0;
      const y0 = Math.max(0, y - kernel);
      const y1 = Math.min(height - 1, y + kernel);
      for (let iy = y0; iy <= y1; iy += 1) {
        const v = temp[iy * width + x];
        if (Number.isFinite(v)) {
          sum += v;
          count += 1;
        }
      }
      output[y * width + x] = count > 0 ? sum / count : Number.NaN;
    }
  }

  return output;
}

export function computeStationsBounds(
  stations: RainfallStation[],
  paddingRatio = 0.08
): GeoBounds | null {
  if (!stations.length) {
    return null;
  }

  let south = stations[0].lat;
  let north = stations[0].lat;
  let west = stations[0].lng;
  let east = stations[0].lng;

  stations.forEach((station) => {
    south = Math.min(south, station.lat);
    north = Math.max(north, station.lat);
    west = Math.min(west, station.lng);
    east = Math.max(east, station.lng);
  });

  const latPad = Math.max((north - south) * paddingRatio, 0.01);
  const lngPad = Math.max((east - west) * paddingRatio, 0.01);

  return {
    south: south - latPad,
    north: north + latPad,
    west: west - lngPad,
    east: east + lngPad,
  };
}

export function resolveOverlayBounds(
  stations: RainfallStation[],
  properties?: {
    latitudeMinima?: number;
    latitudeMaxima?: number;
    longitudeMinima?: number;
    longitudeMaxima?: number;
  } | null
): GeoBounds | null {
  const fromProps =
    properties &&
    Number.isFinite(properties.latitudeMinima) &&
    Number.isFinite(properties.latitudeMaxima) &&
    Number.isFinite(properties.longitudeMinima) &&
    Number.isFinite(properties.longitudeMaxima)
      ? {
          south: properties.latitudeMinima as number,
          north: properties.latitudeMaxima as number,
          west: properties.longitudeMinima as number,
          east: properties.longitudeMaxima as number,
        }
      : null;

  if (fromProps) {
    const latPad = Math.max((fromProps.north - fromProps.south) * 0.02, 0.005);
    const lngPad = Math.max((fromProps.east - fromProps.west) * 0.02, 0.005);
    return {
      south: fromProps.south - latPad,
      north: fromProps.north + latPad,
      west: fromProps.west - lngPad,
      east: fromProps.east + lngPad,
    };
  }

  return computeStationsBounds(stations);
}
